import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
// src/components/grid/TeacherAssignmentModal.tsx
import { X, Search, Clock, AlertCircle, PlusIcon, Trash2, Filter, Star, MessageSquare, Flag, Bookmark, RefreshCw } from 'lucide-react';
import { Staff } from '../../contexts/DataContext';
import { ClassAllocation, LineDefinition } from '../../types/grid';
import { DayOffPreference } from '../../types/intentionPeriods';
import { useStabilizedAllocation } from '../../contexts/StabilizedContexts';
import { getAllowancesForSemester, getTotalAllowanceMinutes } from '../../types/staff';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { displayLearningArea } from '../../constants/learningAreas';
import { DEFAULT_BASE_MINUTES_PER_FORTNIGHT, DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';
import { 
  calculatePreferenceStrength, 
  getPreferenceIndicator, 
  TeacherPreferenceMatch,
  CurriculumSubject 
} from '../../utils/preferenceMatching';
import { TeachingIntention } from '../../types/intentions';
import AdditionalCommentsModal from '../intentions/AdditionalCommentsModal';
import { historicTeachingService, TeachingHistorySummary } from '../../services/historicTeachingService';
// New interface for teacher assignments with minutes
interface TeacherAssignment {
  teacherCode: string;
  minutes: number;
}

// Import flag types
import { CellFlag, FLAG_COLORS, FlagColor } from '../../types/flags';
// Map a dayOffPreference choice to the actual days of the week the teacher wants OFF.
// Empty array = teacher works all days; null = unknown (no preference set).
function daysOffFromPreference(pref?: DayOffPreference): string[] | null {
  if (!pref) return null;
  switch (pref) {
    case 'wed-thu':  return ['Wednesday', 'Thursday'];
    case 'mon-fri':  return ['Monday', 'Friday'];
    case 'wed-only': return ['Wednesday'];
    case 'fri-only': return ['Friday'];
    case 'none':     return [];
    case 'discuss':  return null; // unknown until HR conversation happens
    default:         return null;
  }
}

// Determine how a teacher's day-off preference overlaps with a line's operating days.
// Returns 'conflict' when any line day is a day the teacher wants off,
// 'match' when line days are fully outside their days-off,
// 'unknown' when preference is undefined / discuss / line has no days metadata.
function computeDayFit(
  linedays: string[] | undefined,
  dayOffPref: DayOffPreference | undefined
): 'conflict' | 'match' | 'unknown' {
  if (!linedays || linedays.length === 0) return 'unknown';
  const daysOff = daysOffFromPreference(dayOffPref);
  if (daysOff === null) return 'unknown';
  if (daysOff.length === 0) return 'match'; // no preference / no days off => no conflict
  const hasConflict = linedays.some(d => daysOff.includes(d));
  return hasConflict ? 'conflict' : 'match';
}

interface TeacherAssignmentModalProps {
  classItem: ClassAllocation;
  lineId: string;
  line?: LineDefinition;
  staff: Staff[];
  onAssign: (teacherCode: string | null) => void;
  onMultiAssign?: (assignments: TeacherAssignment[]) => void;
  onClose: () => void;
  // New optional props for preference matching
  teachingIntentions?: TeachingIntention[];
  curriculumSubjects?: CurriculumSubject[];
  // Preference loading props
  preferencesLoading?: boolean;
  onLoadPreferences?: () => Promise<void>;
  // Callback to notify parent when comments modal state changes
  onCommentsModalStateChange?: (isOpen: boolean) => void;
  // Cell flagging props
  onFlagCell?: (flag: Omit<CellFlag, 'id' | 'createdAt' | 'createdBy'>) => void;
  onRemoveFlag?: (classId: string, lineId: string) => void;
  currentFlag?: CellFlag | null;
}
const TeacherAssignmentModal: React.FC<TeacherAssignmentModalProps> = ({
  classItem,
  lineId,
  line,
  staff,
  onAssign,
  onMultiAssign,
  onClose,
  teachingIntentions = [],
  curriculumSubjects = [],
  preferencesLoading = false,
  onLoadPreferences,
  onCommentsModalStateChange,
  onFlagCell,
  onRemoveFlag,
  currentFlag
}) => {
  const {
    getTeacherSemesterLoad
  } = useStabilizedAllocation();
  const { workingYear } = useAcademicYear();
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [showPreferenceMatches, setShowPreferenceMatches] = useState(true);
  const [isMultiTeacherMode, setIsMultiTeacherMode] = useState(false);
  // For multi-teacher mode
  const [teacherAssignments, setTeacherAssignments] = useState<TeacherAssignment[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [teacherMinutes, setTeacherMinutes] = useState<number>(0);
  const [totalMinutes, setTotalMinutes] = useState<number>(DEFAULT_MINUTES_PER_LINE); // Default is DEFAULT_MINUTES_PER_LINE minutes per fortnight
  
  // Comments modal state
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedCommentsStaff, setSelectedCommentsStaff] = useState<Staff | null>(null);

  // Flagging state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [selectedFlagColor, setSelectedFlagColor] = useState<FlagColor>('pink');
  const [flagNote, setFlagNote] = useState('');

  // v1.4.0: historic teaching summaries (keyed by staff code). Empty until the
  // edge function that pulls from Synergetic has populated staff_teaching_history.
  const [historyByStaff, setHistoryByStaff] = useState<Map<string, TeachingHistorySummary[]>>(new Map());

  // Load history in bulk when staff list is known. Runs silently — no spinner,
  // no errors surfaced; history is a nice-to-have garnish, not a blocker.
  useEffect(() => {
    if (!staff || staff.length === 0) return;
    const staffCodes = staff.map(s => s.initial).filter((x): x is string => !!x);
    if (staffCodes.length === 0) return;
    historicTeachingService
      .getSummariesForStaffList(staffCodes, 3)
      .then(map => setHistoryByStaff(map))
      .catch(() => { /* silent — table may be empty */ });
  }, [staff]);

  // Load preferences when modal opens if they haven't been loaded yet
  useEffect(() => {
    const hasPreferences = teachingIntentions.length > 0 || curriculumSubjects.length > 0;
    if (!hasPreferences && !preferencesLoading && onLoadPreferences) {
      onLoadPreferences();
    }
  }, [teachingIntentions, curriculumSubjects, preferencesLoading, onLoadPreferences]);

  // Notify parent component when comments modal state changes
  useEffect(() => {
    onCommentsModalStateChange?.(showCommentsModal);
  }, [showCommentsModal, onCommentsModalStateChange]);

  // Initialize flag modal with existing flag data
  useEffect(() => {
    if (currentFlag) {
      setSelectedFlagColor(currentFlag.color);
      setFlagNote(currentFlag.note);
    } else {
      setSelectedFlagColor('pink');
      setFlagNote('');
    }
  }, [currentFlag]);

  // Flag cell handler
  const handleFlagCell = () => {
    if (!onFlagCell) return;
    
    onFlagCell({
      classId: classItem.id,
      lineId,
      color: selectedFlagColor,
      note: flagNote.trim()
    });
    
    setShowFlagModal(false);
    setFlagNote('');
  };

  // Remove flag handler
  const handleRemoveFlag = () => {
    if (!onRemoveFlag) {
      return;
    }

    onRemoveFlag(classItem.id, lineId);
    setShowFlagModal(false);
    setFlagNote('');
  };

  // Open flag modal handler
  const handleOpenFlagModal = () => {
    setShowFlagModal(true);
  };
  // Get the current teacher assignment
  const currentTeacherCode = classItem.lineAssignments?.[lineId] || null;
  // Initialize multi-teacher mode if there's already a multi-teacher assignment
  useEffect(() => {
    // Check if we have a multi-teacher assignment (stored as JSON string)
    if (currentTeacherCode && (currentTeacherCode.startsWith('[') || currentTeacherCode.startsWith('{'))) {
      try {
        const parsedAssignments = JSON.parse(currentTeacherCode);
        if (Array.isArray(parsedAssignments) && parsedAssignments.length > 0) {
          setTeacherAssignments(parsedAssignments);
          setIsMultiTeacherMode(true);
        } else if (typeof parsedAssignments === 'object' && parsedAssignments.teacherCode) {
          // Handle single object format
          setTeacherAssignments([parsedAssignments]);
          setIsMultiTeacherMode(true);
        }
      } catch (e) {
        console.error('Failed to parse multi-teacher assignment:', e);
        // If parsing fails, treat it as a single teacher code
        setTeacherAssignments([{
          teacherCode: currentTeacherCode,
          minutes: DEFAULT_MINUTES_PER_LINE
        }]);
      }
    } else if (currentTeacherCode) {
      // Single teacher assignment
      setTeacherAssignments([{
        teacherCode: currentTeacherCode,
        minutes: DEFAULT_MINUTES_PER_LINE
      }]);
    }
  }, [currentTeacherCode]);
  // Helper function to extract first name for sorting
  const getFirstName = (fullName: string): string => {
    if (!fullName) return '';

    // Handle "Last, First" format (e.g., "Smith, John")
    if (fullName.includes(',')) {
      const parts = fullName.split(',');
      return parts[1]?.trim() || '';
    }

    // Handle "First Last" format (e.g., "John Smith")
    const parts = fullName.trim().split(/\s+/);
    return parts[0] || '';
  };

  // Get the semester based on the line ID
  const lineSemester = useMemo(() => {
    // Use a simple pattern to determine semester from line ID
    if (!lineId || typeof lineId !== 'string') return 'semester1';

    if (lineId.includes('S1') || ['A', 'B', 'C', 'D', 'E', 'F'].includes(lineId) || lineId.includes('semester1')) {
      return 'semester1';
    }
    if (lineId.includes('S2') || ['G', 'H', 'I', 'J', 'K', 'L'].includes(lineId) || lineId.includes('semester2')) {
      return 'semester2';
    }
    // Default to semester1 if we can't determine
    return 'semester1';
  }, [lineId]);
  // Filter and sort staff based on search and availability
  const filteredStaff = useMemo(() => {

    // Calculate preference matches for all staff
    const staffWithPreferences = staff.map(teacher => {
      const teacherCode = teacher.initial || teacher.code || '';

      const preferenceMatch = calculatePreferenceStrength(
        teacherCode,
        classItem.className || '',
        teachingIntentions,
        curriculumSubjects,
        // The grid knows this class's learning area — pass it so a teacher who
        // nominated the area (rather than the specific subject) still matches.
        classItem.curriculumArea
      );

      return {
        ...teacher,
        preferenceMatch
      };
    });

    // First filter by search query
    let filtered = staffWithPreferences.filter(teacher => {
      const searchString = `${teacher.name} ${teacher.initial} ${teacher.department}`.toLowerCase();
      const matches = searchString.includes(searchQuery.toLowerCase());

      return matches;
    });

    // Then filter by availability if option is selected
    if (showOnlyAvailable) {
      filtered = filtered.filter(teacher => {
        const load = getTeacherSemesterLoad(teacher.initial || '', lineSemester);
        const available = load.utilization < 100;

        return available;
      });
    }

    // Sort by preference strength and match score
    // Always sort by preference first, then by other criteria
    const sorted = filtered.sort((a, b) => {
      // If preference matches are shown, prioritize by preference strength
      if (showPreferenceMatches) {
        // First, sort by preference strength (strong > medium > weak > avoid)
        const strengthOrder = { 'strong': 4, 'medium': 3, 'weak': 2, 'avoid': 1 };
        const aStrength = strengthOrder[a.preferenceMatch.strength] || 0;
        const bStrength = strengthOrder[b.preferenceMatch.strength] || 0;
        
        if (aStrength !== bStrength) {
          return bStrength - aStrength; // Higher strength first
        }
        
        // Within same strength, sort by match score
        if (a.preferenceMatch.matchScore !== b.preferenceMatch.matchScore) {
          return b.preferenceMatch.matchScore - a.preferenceMatch.matchScore; // Higher score first
        }
        
        // Check for specific preference ranks (lower rank = higher priority)
        const aRank = a.preferenceMatch.selfPreferenceRank || 999;
        const bRank = b.preferenceMatch.selfPreferenceRank || 999;
        if (aRank !== bRank) {
          return aRank - bRank; // Lower rank number first (rank 1 before rank 2)
        }
      }
      
      // Fall back to first name sorting (alphabetically, ignoring department)
      return getFirstName(a.name).localeCompare(getFirstName(b.name));
    });
    
    
    return sorted;
  }, [staff, searchQuery, showOnlyAvailable, showPreferenceMatches, getTeacherSemesterLoad, lineSemester, classItem, teachingIntentions, curriculumSubjects]);
  // Get teacher load details including allowances
  const getTeacherLoadDetails = (teacherCode: string) => {
    const load = getTeacherSemesterLoad(teacherCode, lineSemester);

    // Find the staff member to get allowance information
    const teacher = staff.find(s => s.initial === teacherCode || s.code === teacherCode);

    if (teacher) {
      // Get allowances for the current semester
      const allowances = getAllowancesForSemester(teacher, lineSemester, workingYear);
      const allowanceMinutes = getTotalAllowanceMinutes(teacher, lineSemester, workingYear);

      // Calculate available minutes (FTE × base minutes - allowances)
      const availableMinutesPerSemester = (teacher.fte * DEFAULT_BASE_MINUTES_PER_FORTNIGHT);
      const adjustedAvailableMinutes = availableMinutesPerSemester - allowanceMinutes;

      // Recalculate utilization based on adjusted available minutes
      const adjustedUtilization = adjustedAvailableMinutes > 0
        ? Math.round((load.allocatedMinutes / adjustedAvailableMinutes) * 100)
        : 0;

      return {
        allocatedMinutes: load.allocatedMinutes,
        availableMinutes: adjustedAvailableMinutes,
        utilization: adjustedUtilization,
        overloaded: adjustedUtilization > 100,
        allowances: allowances,
        allowanceMinutes: allowanceMinutes,
        baseAvailableMinutes: availableMinutesPerSemester
      };
    }

    // Fallback to original load if teacher not found
    return {
      allocatedMinutes: load.allocatedMinutes,
      availableMinutes: load.availableMinutes,
      utilization: load.utilization,
      overloaded: load.overloaded,
      allowances: [],
      allowanceMinutes: 0,
      baseAvailableMinutes: load.availableMinutes
    };
  };
  // Get utilization color
  const getUtilizationColor = (utilization: number) => {
    if (utilization > 100) return 'text-danger-fg';
    if (utilization > 90) return 'text-warn-fg';
    return 'text-ok-fg';
  };
  // Handle key events for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const modal = document.getElementById('teacher-assignment-modal');
      if (modal && !modal.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  // Calculate remaining minutes
  const remainingMinutes = useMemo(() => {
    const usedMinutes = teacherAssignments.reduce((sum, assignment) => sum + assignment.minutes, 0);
    return totalMinutes - usedMinutes;
  }, [teacherAssignments, totalMinutes]);
  // Handle adding a teacher to multi-teacher assignment
  const handleAddTeacher = () => {
    if (!selectedTeacher || teacherMinutes <= 0 || teacherMinutes > remainingMinutes) return;
    // Check if teacher is already assigned
    if (teacherAssignments.some(a => a.teacherCode === selectedTeacher)) {
      // Update existing assignment
      setTeacherAssignments(prev => prev.map(a => a.teacherCode === selectedTeacher ? {
        ...a,
        minutes: a.minutes + teacherMinutes
      } : a));
    } else {
      // Add new assignment
      setTeacherAssignments(prev => [...prev, {
        teacherCode: selectedTeacher,
        minutes: teacherMinutes
      }]);
    }
    // Reset form
    setSelectedTeacher(null);
    setTeacherMinutes(0);
  };
  // Handle removing a teacher from multi-teacher assignment
  const handleRemoveTeacher = (teacherCode: string) => {
    setTeacherAssignments(prev => prev.filter(a => a.teacherCode !== teacherCode));
  };
  // Handle saving multi-teacher assignment
  const handleSaveMultiAssignment = () => {
    if (onMultiAssign && teacherAssignments.length > 0) {
      onMultiAssign(teacherAssignments);
    } else if (teacherAssignments.length === 1) {
      // If only one teacher, use the simple assignment method
      onAssign(teacherAssignments[0].teacherCode);
    } else {
      // Clear assignment if no teachers
      onAssign(null);
    }
    onClose();
  };
  // Get teacher name from code
  const getTeacherName = (code: string) => {
    const teacher = staff.find(s => s.initial === code);
    return teacher ? teacher.name : code;
  };

  // Check if a staff member has additional comments
  const hasAdditionalComments = (staffInitial: string) => {
    // Try exact match first
    let intention = teachingIntentions.find(intention => intention.staffId === staffInitial);
    
    // If no exact match and staffInitial is 3+ chars, try 2-char fallback
    if (!intention && staffInitial && staffInitial.length >= 3) {
      const shortCode = staffInitial.substring(0, 2);
      intention = teachingIntentions.find(intention => intention.staffId === shortCode);
    }
    
    return intention && intention.additionalComments && intention.additionalComments.trim().length > 0;
  };

  // Get additional comments for a staff member
  const getAdditionalComments = (staffInitial: string) => {
    // Try exact match first
    let intention = teachingIntentions.find(intention => intention.staffId === staffInitial);

    // If no exact match and staffInitial is 3+ chars, try 2-char fallback
    if (!intention && staffInitial && staffInitial.length >= 3) {
      const shortCode = staffInitial.substring(0, 2);
      intention = teachingIntentions.find(intention => intention.staffId === shortCode);
    }

    return intention?.additionalComments || '';
  };

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num: number): string => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const remainder = num % 100;
    return num + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
  };

  // Helper function to get teacher self-preferences for display
  const getTeacherSelfPreferences = (staffInitial: string) => {
    // Try exact match first
    let intention = teachingIntentions.find(intention => intention.staffId === staffInitial);

    // If no exact match and staffInitial is 3+ chars, try 2-char fallback
    if (!intention && staffInitial && staffInitial.length >= 3) {
      const shortCode = staffInitial.substring(0, 2);
      intention = teachingIntentions.find(intention => intention.staffId === shortCode);
    }


    if (!intention || !intention.teachingPreferences) {
      return [];
    }

    // Handle both string arrays and object arrays
    if (Array.isArray(intention.teachingPreferences)) {
      return intention.teachingPreferences.slice(0, 1); // Only show first preference for inline display
    }

    return [];
  };

  // Helper function to format preference for display (handle long names)
  const formatPreferenceForDisplay = (preference: { specificSubject?: string; learningArea?: string }, maxLength = 15): string => {
    const subject = preference.specificSubject || preference.learningArea || 'Unknown';
    if (subject.length <= maxLength) {
      return subject;
    }
    return subject.substring(0, maxLength - 3) + '...';
  };

  // Helper function to get full teaching intention for a staff member
  const getTeachingIntention = (staffInitial: string) => {
    // Try exact match first
    let intention = teachingIntentions.find(intention => intention.staffId === staffInitial);

    // If no exact match and staffInitial is 3+ chars, try 2-char fallback
    if (!intention && staffInitial && staffInitial.length >= 3) {
      const shortCode = staffInitial.substring(0, 2);
      intention = teachingIntentions.find(intention => intention.staffId === shortCode);
    }

    return intention || null;
  };

  // Helper function to get teacher availability info from their intention
  const getTeacherAvailability = (staffInitial: string) => {
    const intention = getTeachingIntention(staffInitial);
    if (!intention) return null;

    // Get FTE - prefer new availability structure, fall back to legacy fields
    const intendedFTE = intention.availability?.fte ?? intention.targetFTE ?? intention.currentFTE;

    // Get days available from new structure
    const daysAvailable = intention.availability?.daysAvailable || [];

    // Get periods unavailable from new structure
    const periodsUnavailable = intention.availability?.periodsUnavailable || [];

    // Check employment details for return intention
    const employmentDetails = intention.employmentDetails;
    const isReturning = employmentDetails?.employmentType === 'ongoing'
      ? employmentDetails?.intentionToReturn
      : employmentDetails?.wishToBeConsidered;

    // Get leave plans
    const hasLeavePlans = employmentDetails?.hasLeavePlans || false;
    const leaveType = employmentDetails?.leaveType;

    return {
      intendedFTE,
      daysAvailable,
      periodsUnavailable,
      isReturning,
      hasLeavePlans,
      leaveType,
      // Helper to check if all weekdays are available
      isFullWeek: daysAvailable.length >= 5,
      // Helper to format days as short names
      daysShort: daysAvailable.map(d => d.substring(0, 3).charAt(0).toUpperCase() + d.substring(1, 3))
    };
  };

  // Handle showing comments modal
  const handleShowComments = (staff: Staff) => {
    const staffId = staff.staff_id || staff.initial || '';
    const comments = getAdditionalComments(staffId);
    // Use requestAnimationFrame to ensure this happens after current event processing
    requestAnimationFrame(() => {
      setSelectedCommentsStaff(staff);
      setShowCommentsModal(true);
    });
  };
  // Toggle between single and multi-teacher modes
  const toggleMultiTeacherMode = () => {
    if (!isMultiTeacherMode && currentTeacherCode) {
      // Convert single teacher to multi-teacher format
      setTeacherAssignments([{
        teacherCode: currentTeacherCode,
        minutes: DEFAULT_MINUTES_PER_LINE
      }]);
    }
    setIsMultiTeacherMode(!isMultiTeacherMode);
  };
  // Handle single teacher assignment (original functionality)
  const handleSingleTeacherAssign = (teacherCode: string | null) => {
    onAssign(teacherCode);
    onClose();
  };
  return <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div id="teacher-assignment-modal" className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-ink-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-ink-900">
            Assign Teacher to {classItem.subjectCode 
              ? `${classItem.subjectCode} - ${classItem.className}`
              : classItem.className
            }
          </h3>
          <div className="flex items-center space-x-2">
            {/* Flag Button */}
            {onFlagCell && (
              <button
                onClick={handleOpenFlagModal}
                className={`p-2 rounded-md border transition-colors ${
                  currentFlag 
                    ? `${FLAG_COLORS.find(c => c.id === currentFlag.color)?.bgClass} ${FLAG_COLORS.find(c => c.id === currentFlag.color)?.borderClass} ${FLAG_COLORS.find(c => c.id === currentFlag.color)?.iconClass}` 
                    : 'border-ink-300 text-ink-400 hover:text-ink-500 hover:border-ink-400'
                }`}
                title={currentFlag ? `Flagged: ${currentFlag.note || 'No note'}` : 'Flag this cell for later review'}
              >
                <Flag className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="text-ink-400 hover:text-ink-500">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-4 border-b border-ink-200 bg-ink-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-ink-700">
                Class: {classItem.subjectCode 
                  ? `${classItem.subjectCode} - ${classItem.className}`
                  : classItem.className
                }
              </div>
              <div className="text-sm text-ink-500">
                Line: {lineId} • Semester:{' '}
                {lineSemester === 'semester1' ? '1' : '2'}
              </div>
            </div>
            {/* Toggle for multi-teacher mode */}
            <div className="flex items-center">
              <button onClick={toggleMultiTeacherMode} className={`px-3 py-1 rounded-md text-sm font-medium ${isMultiTeacherMode ? 'bg-navy-100 text-navy-800 border border-navy-300' : 'bg-ink-100 text-ink-900 border border-ink-300'}`}>
                {isMultiTeacherMode ? 'Multi-Teacher Mode' : 'Single Teacher Mode'}
              </button>
            </div>
          </div>
        </div>

        {/* Multi-teacher assignment UI */}
        {isMultiTeacherMode && <div className="p-4 border-b border-ink-200 bg-navy-50">
            <h4 className="text-sm font-medium text-ink-700 mb-2">
              Current Assignments
            </h4>
            {teacherAssignments.length === 0 ? <p className="text-sm text-ink-500">No teachers assigned yet.</p> : <div className="space-y-2">
                {teacherAssignments.map(assignment => <div key={assignment.teacherCode} className="flex items-center justify-between bg-white p-2 rounded border border-ink-200">
                    <div>
                      <span className="font-medium">
                        {getTeacherName(assignment.teacherCode)}
                      </span>
                      <span className="ml-2 font-mono tabular-nums text-sm bg-ink-100 px-2 py-0.5 rounded text-ink-700">
                        {assignment.teacherCode}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-mono tabular-nums text-sm text-ink-500 mr-3">
                        {assignment.minutes} minutes (
                        {Math.round(assignment.minutes / totalMinutes * 100)}
                        %)
                      </span>
                      <button onClick={() => handleRemoveTeacher(assignment.teacherCode)} className="text-danger-fg hover:text-danger-fg">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>)}
                <div className="flex justify-between text-sm font-medium mt-2">
                  <span>Total:</span>
                  <span className="font-mono tabular-nums">
                    {teacherAssignments.reduce((sum, a) => sum + a.minutes, 0)}{' '}
                    / {totalMinutes} minutes (
                    {Math.round(teacherAssignments.reduce((sum, a) => sum + a.minutes, 0) / totalMinutes * 100)}
                    %)
                  </span>
                </div>
              </div>}
            {remainingMinutes > 0 && <div className="mt-4 p-3 border border-ink-200 rounded bg-white">
                <h4 className="text-sm font-medium text-ink-700 mb-2">
                  Add Teacher
                </h4>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select className="border border-ink-300 rounded-md px-3 py-1 text-sm flex-grow" value={selectedTeacher || ''} onChange={e => setSelectedTeacher(e.target.value || null)}>
                    <option value="">Select a teacher</option>
                    {filteredStaff.map(teacher => <option key={teacher.id} value={teacher.initial}>
                        {teacher.name} ({teacher.initial})
                      </option>)}
                  </select>
                  <div className="flex items-center">
                    <input type="number" min="1" max={remainingMinutes} value={teacherMinutes} onChange={e => setTeacherMinutes(parseInt(e.target.value) || 0)} className="border border-ink-300 rounded-md px-3 py-1 text-sm w-20" placeholder="Minutes" />
                    <span className="mx-2 text-sm text-ink-500">
                      of {remainingMinutes} remaining
                    </span>
                  </div>
                  <button onClick={handleAddTeacher} disabled={!selectedTeacher || teacherMinutes <= 0 || teacherMinutes > remainingMinutes} className="px-3 py-1 bg-navy-600 text-white rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                    <PlusIcon className="h-4 w-4 inline mr-1" />
                    Add
                  </button>
                </div>
              </div>}
          </div>}

        <div className="p-4 border-b border-ink-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-ink-400" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" placeholder="Search teachers by name, code, or learning area..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input id="show-available" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" checked={showOnlyAvailable} onChange={e => setShowOnlyAvailable(e.target.checked)} />
                <label htmlFor="show-available" className="ml-2 block text-sm text-ink-900">
                  Available only
                </label>
              </div>
              <div className="flex items-center">
                <input id="show-preferences" type="checkbox" className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded" checked={showPreferenceMatches} onChange={e => setShowPreferenceMatches(e.target.checked)} />
                <label htmlFor="show-preferences" className="ml-2 block text-sm text-ink-900">
                  <Filter className="h-4 w-4 inline mr-1" />
                  Show preferences
                  {showPreferenceMatches && <span className="ml-1 text-xs text-ok-fg font-medium">(Active)</span>}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-1">
          {preferencesLoading ? <div className="flex flex-col items-center justify-center py-12 px-4">
              <RefreshCw className="h-8 w-8 text-navy-500 mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-ink-900 mb-2">
                Loading teacher preferences...
              </h3>
              <p className="text-ink-500 text-center">
                Please wait while we load preference data.
              </p>
            </div> : filteredStaff.length === 0 ? <div className="flex flex-col items-center justify-center py-12 px-4">
              <Search className="h-12 w-12 text-ink-300 mb-4" />
              <h3 className="text-lg font-medium text-ink-900 mb-2">
                No teachers found
              </h3>
              <p className="text-ink-500 text-center">
                Try adjusting your search or filters to find teachers.
              </p>
            </div> : <div className="divide-y divide-ink-200">
              {filteredStaff.map(teacher => {
            const {
              allocatedMinutes,
              availableMinutes,
              utilization,
              overloaded,
              allowances,
              allowanceMinutes,
              baseAvailableMinutes
            } = getTeacherLoadDetails(teacher.initial || '');
            // Check if this teacher is already in the multi-teacher assignment
            const existingAssignment = teacherAssignments.find(a => a.teacherCode === teacher.initial);
            const indicator = getPreferenceIndicator(teacher.preferenceMatch.strength);
            
            return <div key={teacher.id} className={`p-4 hover:bg-ink-50 cursor-pointer transition-colors ${
              currentTeacherCode === teacher.initial || existingAssignment ? 'bg-navy-50 border-l-4 border-navy-500' : 
              teacher.preferenceMatch.selfPreferenceRank === 1 ? 'bg-ok-bg border-l-4 border-ok-border' : 
              teacher.preferenceMatch.strength === 'strong' ? 'bg-ok-bg/50 border-l-2 border-ok-border' :
              ''
            }`} onClick={() => {
              if (isMultiTeacherMode) {
                // In multi-teacher mode, set as selected teacher
                setSelectedTeacher(teacher.initial || null);
                setTeacherMinutes(remainingMinutes > 0 ? remainingMinutes : 0);
              } else {
                // In single-teacher mode, use original behavior
                handleSingleTeacherAssign(teacher.initial || '');
              }
            }}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <div className="font-medium text-ink-900">
                            {teacher.name}
                          </div>
                          
                          {/* Preference indicator - made more prominent */}
                          {teacher.preferenceMatch.strength !== 'weak' && (
                            <div className={`flex items-center ml-2 px-2 py-1 rounded-full text-xs font-semibold ${indicator.bg} ${indicator.color} ${indicator.border} border`}>
                              <span className="mr-1">{indicator.icon}</span>
                              <span>{indicator.label}</span>
                              {/* Show rank if it's a top preference */}
                              {teacher.preferenceMatch.selfPreferenceRank && teacher.preferenceMatch.selfPreferenceRank <= 3 && (
                                <span className="font-mono tabular-nums ml-1 font-bold">
                                  #{teacher.preferenceMatch.selfPreferenceRank}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Special indicator for first preference */}
                          {teacher.preferenceMatch.selfPreferenceRank === 1 && (
                            <div className="ml-1 flex items-center px-2 py-1 rounded-full text-xs font-bold bg-warn-bg text-warn-fg border border-warn-border">
                              <Star className="h-3 w-3 mr-1" />
                              1st Choice
                            </div>
                          )}
                          <div className="ml-2 font-mono tabular-nums text-sm bg-ink-100 px-2 py-0.5 rounded text-ink-700">
                            {teacher.initial}
                          </div>

                          {/* Late / Discuss / Day-fit badges (v1.2.0 + v1.4.0) */}
                          {(() => {
                            const _intention = getTeachingIntention(teacher.initial || '');
                            const _dayFit = computeDayFit(line?.days, _intention?.availability?.dayOffPreference);
                            return (
                              <>
                                {_intention?.isLate && (
                                  <span
                                    className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-warn-bg text-warn-fg border border-warn-border"
                                    title="Submitted after the due date"
                                  >
                                    Late
                                  </span>
                                )}
                                {_intention?.hrDiscussFlag && (
                                  <span
                                    className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-navy-100 text-navy-800 border border-navy-300"
                                    title="Teacher requested to discuss FTE / day-off preferences with HR"
                                  >
                                    Discuss
                                  </span>
                                )}
                                {_dayFit === 'conflict' && (
                                  <span
                                    className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-danger-bg text-danger-fg border border-danger-border"
                                    title={`This line operates on ${line?.days?.join(', ')} — teacher prefers those day(s) off (${_intention?.availability?.dayOffPreference})`}
                                  >
                                    Day off conflict
                                  </span>
                                )}
                                {(() => {
                                  // v1.4.0 Phase 6 scaffold: show "Previously taught" chip if we have history
                                  // that matches the current class's subject name. Degrades to nothing
                                  // until Synergetic fetch is wired.
                                  const summaries = historyByStaff.get(teacher.initial || '') || [];
                                  if (summaries.length === 0) return null;
                                  const classSubject = (classItem.className || classItem.subjectCode || '').toLowerCase();
                                  const matching = summaries.find(s =>
                                    s.label.toLowerCase().includes(classSubject) ||
                                    classSubject.includes(s.label.toLowerCase())
                                  );
                                  if (!matching) return null;
                                  return (
                                    <span
                                      className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-ok-bg text-ok-fg border border-ok-border"
                                      title={`Taught "${matching.label}" in ${matching.years.join(', ')}`}
                                    >
                                      Taught {matching.yearCount}× ({matching.yearRange})
                                    </span>
                                  );
                                })()}
                              </>
                            );
                          })()}

                          {/* Additional Comments Icon - Now shown for ALL staff */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowComments(teacher);
                            }}
                            className={`ml-2 p-1 rounded transition-colors ${
                              hasAdditionalComments(teacher.staff_id || teacher.initial || '') || getTeachingIntention(teacher.initial || '')
                                ? 'text-navy-600 hover:text-navy-800 hover:bg-navy-100'
                                : 'text-ink-400 hover:text-ink-500 hover:bg-ink-100'
                            }`}
                            title={
                              hasAdditionalComments(teacher.staff_id || teacher.initial || '') || getTeachingIntention(teacher.initial || '')
                                ? 'View teacher preferences and comments'
                                : 'View teacher information (no preferences submitted)'
                            }
                          >
                            <MessageSquare size={16} />
                          </button>
                          
                          {currentTeacherCode === teacher.initial && !isMultiTeacherMode && <div className="ml-2 text-xs font-medium text-navy-800 bg-navy-100 px-2 py-0.5 rounded-full">
                                Current
                              </div>}
                          {existingAssignment && <div className="ml-2 text-xs font-medium text-navy-800 bg-navy-100 px-2 py-0.5 rounded-full">
                              Assigned ({existingAssignment.minutes} min)
                            </div>}
                        </div>
                        {/* Department and FTE with intention info */}
                        {(() => {
                          const availability = getTeacherAvailability(teacher.initial || '');
                          const currentFTE = teacher.fte;
                          const intendedFTE = availability?.intendedFTE;
                          const fteChanged = intendedFTE !== undefined && Math.abs(intendedFTE - currentFTE) > 0.05;

                          return (
                            <div className="text-sm text-ink-500 mt-1 space-y-1">
                              <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                                <span>{displayLearningArea(teacher.department)}</span>
                                <span>•</span>
                                <span className={`font-mono tabular-nums ${fteChanged ? 'text-warn-fg font-medium' : ''}`}>
                                  {currentFTE.toFixed(1)} FTE
                                  {fteChanged && intendedFTE !== undefined && (
                                    <span className="ml-1">
                                      → <span className="text-navy-600">{intendedFTE.toFixed(1)}</span> intended
                                    </span>
                                  )}
                                </span>
                                {/* Warning indicators */}
                                {availability?.isReturning === 'no' && (
                                  <span className="px-1.5 py-0.5 bg-danger-bg text-danger-fg text-xs rounded font-medium">
                                    Not returning
                                  </span>
                                )}
                                {availability?.isReturning === 'uncertain' && (
                                  <span className="px-1.5 py-0.5 bg-warn-bg text-warn-fg text-xs rounded font-medium">
                                    Uncertain
                                  </span>
                                )}
                                {availability?.hasLeavePlans && (
                                  <span className="px-1.5 py-0.5 bg-navy-100 text-navy-700 text-xs rounded font-medium">
                                    Leave planned
                                  </span>
                                )}
                              </div>
                              {/* Days available - show if not full week */}
                              {availability && !availability.isFullWeek && availability.daysAvailable.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-ink-400">Days:</span>
                                  {availability.daysShort.map((day, idx) => (
                                    <span key={idx} className="px-1.5 py-0.5 bg-navy-50 text-navy-700 text-xs rounded font-medium">
                                      {day}
                                    </span>
                                  ))}
                                  {availability.daysAvailable.length < 5 && (
                                    <span className="text-xs text-warn-fg font-medium ml-1">
                                      (part-time)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* Show curriculum leader preference reasons */}
                        {teacher.preferenceMatch.reasons.length > 0 && (
                          <div className={`mt-1 text-xs font-medium ${
                            teacher.preferenceMatch.strength === 'strong' ? 'text-ok-fg' :
                            teacher.preferenceMatch.strength === 'medium' ? 'text-warn-fg' :
                            'text-ink-500'
                          }`}>
                            💡 {teacher.preferenceMatch.reasons.join(' • ')}
                            {teacher.preferenceMatch.selfPreferenceRank === 1 && (
                              <span className="ml-1 font-bold text-ok-fg">
                                (Top teaching preference!)
                              </span>
                            )}
                          </div>
                        )}
                        {/* Show teacher self-preferences - independent of subject */}
                        {(() => {
                          const selfPreferences = getTeacherSelfPreferences(teacher.initial || '');
                          if (selfPreferences.length === 0) return null;

                          // Show only the first preference
                          const firstPref = selfPreferences[0];
                          const subject = typeof firstPref === 'string' ? firstPref : (firstPref.specificSubject || firstPref.learningArea || 'Not specified');
                          const totalPrefs = getTeachingIntention(teacher.initial || '')?.teachingPreferences?.length || 0;

                          return (
                            <div className="mt-1 text-xs text-navy-600 font-medium">
                              👤 {subject}
                              {totalPrefs > 1 && (
                                <span className="ml-1 text-ink-500">+{totalPrefs - 1} more</span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end">
                          <Clock className="h-4 w-4 text-ink-400 mr-1" />
                          <span className={`font-mono tabular-nums text-sm font-medium ${getUtilizationColor(utilization)}`}>
                            {utilization}%
                          </span>
                        </div>
                        <div className="font-mono tabular-nums text-xs text-ink-500 mt-1">
                          {allocatedMinutes}/{availableMinutes} minutes
                        </div>
                        {allowanceMinutes > 0 && (
                          <div className="font-mono tabular-nums text-xs text-navy-600 mt-1">
                            -{allowanceMinutes} allowances
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Progress bar with allowance visualization */}
                    <div className="mt-2">
                      <div className="w-full bg-ink-200 rounded-full h-2 relative">
                        {/* Main allocation bar */}
                        <div className={`h-2 rounded-full ${overloaded ? 'bg-danger-solid' : utilization > 90 ? 'bg-warn-solid' : 'bg-ok-solid'}`} style={{
                    width: `${Math.min(100, utilization)}%`
                  }} />
                        {/* Allowance indicator - show as purple section */}
                        {allowanceMinutes > 0 && baseAvailableMinutes > 0 && (
                          <div
                            className="absolute top-0 right-0 h-2 bg-navy-400 rounded-r-full opacity-75"
                            style={{
                              width: `${Math.min(30, (allowanceMinutes / baseAvailableMinutes) * 100)}%`
                            }}
                            title={`${allowanceMinutes} minutes in allowances`}
                          />
                        )}
                      </div>
                    </div>
                    {/* Allowance details */}
                    {allowances.length > 0 && (
                      <div className="mt-2 bg-navy-50 rounded p-2">
                        <div className="text-xs font-medium text-navy-700 mb-1">
                          Allowances ({allowanceMinutes} min):
                        </div>
                        <div className="space-y-1">
                          {allowances.slice(0, 2).map((allowance, idx) => (
                            <div key={`${teacher.id}-allowance-${idx}`} className="text-xs text-navy-600 flex justify-between">
                              <span className="truncate">{allowance.name}</span>
                              <span className="font-mono tabular-nums font-medium">{allowance.minutes} min</span>
                            </div>
                          ))}
                          {allowances.length > 2 && (
                            <div className="text-xs text-navy-500">
                              +{allowances.length - 2} more
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Warning if overloaded */}
                    {overloaded && <div className="mt-2 text-xs text-danger-fg flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Teacher is overloaded in{' '}
                        {lineSemester === 'semester1' ? 'Semester 1' : 'Semester 2'}
                      </div>}
                  </div>;
          })}
            </div>}
        </div>

        <div className="p-4 border-t border-ink-200 bg-ink-50 flex justify-between">
          {isMultiTeacherMode ? <>
              <button onClick={() => setTeacherAssignments([])} className="inline-flex justify-center py-2 px-4 border border-danger-border shadow-sm text-sm font-medium rounded-md text-danger-fg bg-white hover:bg-danger-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-solid">
                Clear All
              </button>
              <div>
                <button onClick={onClose} className="inline-flex justify-center py-2 px-4 border border-ink-300 shadow-sm text-sm font-medium rounded-md text-ink-700 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 mr-2">
                  Cancel
                </button>
                <button onClick={handleSaveMultiAssignment} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-navy-600 hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
            // disabled={teacherAssignments.length === 0}
            >
                  Save Assignments
                </button>
              </div>
            </> : <>
              {currentTeacherCode && <button onClick={() => handleSingleTeacherAssign(null)} className="inline-flex justify-center py-2 px-4 border border-danger-border shadow-sm text-sm font-medium rounded-md text-danger-fg bg-white hover:bg-danger-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-solid">
                  Remove Assignment
                </button>}
              <button onClick={onClose} className="inline-flex justify-center py-2 px-4 border border-ink-300 shadow-sm text-sm font-medium rounded-md text-ink-700 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500 ml-auto">
                Cancel
              </button>
            </>}
        </div>
      </div>
    </div>

    {/* Additional Comments Modal - render outside parent modal using portal */}
    {showCommentsModal && selectedCommentsStaff &&
      createPortal(
        <AdditionalCommentsModal
          isOpen={showCommentsModal}
          staffName={selectedCommentsStaff.fullName || selectedCommentsStaff.name}
          staffInitial={selectedCommentsStaff.staff_id || selectedCommentsStaff.initial || ''}
          additionalComments={getAdditionalComments(selectedCommentsStaff.staff_id || selectedCommentsStaff.initial || '')}
          teachingIntention={getTeachingIntention(selectedCommentsStaff.staff_id || selectedCommentsStaff.initial || '')}
          onClose={() => {
            setShowCommentsModal(false);
            setSelectedCommentsStaff(null);
          }}
        />,
        document.body
      )
    }

    {/* Flag Modal - render outside parent modal using portal */}
    {showFlagModal && 
      createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => setShowFlagModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-ink-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-ink-900 flex items-center">
                  <Flag className="h-5 w-5 mr-2 text-ink-500" />
                  Flag Cell for Review
                </h3>
                <button 
                  onClick={() => setShowFlagModal(false)}
                  className="text-ink-400 hover:text-ink-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-4">
                <div className="text-sm text-ink-500 mb-2">
                  Flagging: <span className="font-medium">{classItem.subjectCode} - {classItem.className}</span> on Line {lineId}
                </div>
                
                {currentFlag && (
                  <div className="mb-4 p-3 bg-warn-bg border border-warn-border rounded-md">
                    <div className="text-sm text-warn-fg">
                      <strong>Currently flagged:</strong> {currentFlag.note || 'No note'}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-2">
                    Choose Flag Color
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {FLAG_COLORS.map((color) => (
                      <button
                        key={color.id}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedFlagColor(color.id);
                        }}
                        className={`p-3 rounded-md border-2 transition-all ${
                          selectedFlagColor === color.id
                            ? `${color.borderClass} ${color.bgClass}`
                            : 'border-ink-200 hover:border-ink-300'
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-1">
                          <Flag className={`h-4 w-4 ${color.iconClass}`} />
                          <span className={`text-xs font-medium ${selectedFlagColor === color.id ? color.textClass : 'text-ink-500'}`}>
                            {color.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Note Input */}
                <div>
                  <label htmlFor="flagNote" className="block text-sm font-medium text-ink-700 mb-2">
                    Note (Optional)
                  </label>
                  <textarea
                    id="flagNote"
                    value={flagNote}
                    onChange={(e) => setFlagNote(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    rows={3}
                    className="w-full border border-ink-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    placeholder="Why are you flagging this cell? What needs to be remembered?"
                    maxLength={200}
                  />
                  <div className="text-xs text-ink-500 mt-1">
                    {flagNote.length}/200 characters
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-ink-200 bg-ink-50 flex justify-between">
              {currentFlag && (
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveFlag();
                  }}
                  className="inline-flex items-center px-3 py-2 border border-danger-border shadow-sm text-sm font-medium rounded-md text-danger-fg bg-white hover:bg-danger-bg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-solid"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove Flag
                </button>
              )}
              <div className="flex space-x-2 ml-auto">
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-ink-300 shadow-sm text-sm font-medium rounded-md text-ink-700 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                >
                  Cancel
                </button>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFlagCell();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-navy-600 hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-500"
                >
                  <Bookmark className="h-4 w-4 mr-1" />
                  {currentFlag ? 'Update Flag' : 'Flag Cell'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    }
  </>;
};
export default TeacherAssignmentModal;